import AnalyticsPagesLayout from '@/components/analyticsPagesLayout';
import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import {
  COMPETITOR_ANALYSIS,
  COMPETITOR_ANALYTICS_NAVBAR_ACTIONS,
  SOCIAL_FEEDS
} from '../dashboard/config';
import CommonHeader from '@/components/competitorAnalytics/common';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import { FACEBOOK, INSTAGRAM, PLATFORM_LIST } from '@/config/constants';
import { lomavisCreatorRootState } from '../lomavisCreator';
import { resolve } from '@/api/endPoints';
import axiosClient from '@/utils/axios';
import CompetitorAnalysis from '@/components/competitorAnalytics/competitorAnalysis';
import SocialFeeds from '@/components/competitorAnalytics/socialFeeds';
import { getDefaultRanges } from '@/lomavisSharedUi/atoms/inputs/LomavisRangeCalendar1';
import { getLocationDetails } from '@/redux/slices/location/locationDetails';
import { AnyAction } from 'redux';
import { useParams } from 'react-router-dom';
import { getCurrentLocationUUID } from '@/utils/communicationUtils';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import './index.css';
import { LomavisPlatformIcon } from '@/lomavisSharedUi/atoms/icons/LomavisPlatformIcon';

const index: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const defaultRanges = getDefaultRanges();
  const { locationslug } = useParams();
  const locationDetails = useSelector((state: any) => state.locationDetails);

  const [allCompetitors, setAllCompetitors] = useState([]);
  const [filteredCompetitors, setFilteredCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: defaultRanges[2].startDate,
    endDate: defaultRanges[2].endDate
  });
  const [submittingCompetitor, setSubmittingCompetitor] = useState(false);
  const [removingCompetitorUuid, setRemovingCompetitorUuid] = useState<
    string | null
  >(null);

  const [selectedPlatform, setSelectedPlatform] = useState(0);
  const [platformInformation, setPlatformInformation] = useState(null);
  const [allowedCompetitorProfiles, setAllowedCompetitorProfiles] = useState<
    number | null
  >(null);

  const activePlatforms = useMemo(() => {
    const platforms = new Set(allCompetitors.map((c) => c.platform));

    // Always include facebook and instagram if connected
    if (platformInformation) {
      if (platformInformation[FACEBOOK]) platforms.add(FACEBOOK);
      if (platformInformation[INSTAGRAM]) platforms.add(INSTAGRAM);
    }

    return Array.from(platforms)
      .filter((p): p is string => Boolean(p) && PLATFORM_LIST.includes(p))
      .sort((a, b) => PLATFORM_LIST.indexOf(a) - PLATFORM_LIST.indexOf(b));
  }, [allCompetitors, platformInformation]);

  useEffect(() => {
    if (locationslug ?? '') {
      dispatch(getLocationDetails(locationslug) as unknown as AnyAction);
    }
  }, [dispatch, locationslug]);

  const getCompetitorAnalysis = async ({ uuid }) => {
    try {
      setLoading(true);
      setError(null); // clear any old error state

      let allResults: any[] = [];
      let url = resolve('competitor_analysis', {}, { location__uuid: uuid });

      while (url) {
        const response = await axiosClient.get(url);

        if (response?.data?.results) {
          allResults = [...allResults, ...response.data.results];
        }

        // move to next page
        url = response?.data?.next || null;
      }

      if (allResults.length > 0) {
        setAllCompetitors(allResults);
      } else {
        setError('No competitor data found.');
      }
    } catch (err: any) {
      console.error('Error fetching competitor analysis:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Something went wrong while fetching competitor analysis.'
      );
    } finally {
      setLoading(false);
    }
  };

  function filterPostsByDateRange(allCompetitors, dateRange) {
    const { startDate, endDate } = dateRange;

    // Convert date range to Date objects if they're not already
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Create a deep copy of the array to avoid mutating the original
    const filteredCompetitors = allCompetitors.map((competitor) => {
      // Filter posts_data based on published date
      const filteredPosts = competitor.posts_data.filter((post) => {
        const publishedDate = new Date(post.published);
        return publishedDate >= start && publishedDate <= end;
      });

      // Filter followers_development
      const filteredFollowers =
        competitor.followers_development?.filter((entry) => {
          const entryDate = new Date(entry.date);
          return entryDate >= start && entryDate <= end;
        }) || [];

      // Return the competitor object with filtered posts_data
      return {
        ...competitor,
        posts_data: filteredPosts,
        followers_development: filteredFollowers
      };
    });

    return filteredCompetitors;
  }

  useEffect(() => {
    if (locationDetails?.uuid) {
      getAllowedCompetitorCount();
      setPlatformInformation(locationDetails?.platforms_information);
      getCompetitorAnalysis({ uuid: locationDetails?.uuid });
    }
  }, [locationDetails]);
  useEffect(() => {
    if (allCompetitors.length > 0 && dateRange) {
      // Step 1: filter by date range
      let results = filterPostsByDateRange(allCompetitors, dateRange);

      // Step 2: filter by platform (if selectedPlatform is valid)
      const selectedPlatformKey = activePlatforms[selectedPlatform];
      if (selectedPlatformKey) {
        results = results.filter((c) => c.platform === selectedPlatformKey);
      }

      setFilteredCompetitors(results);
    }
  }, [dateRange, allCompetitors, selectedPlatform, activePlatforms]);

  const removeCompetitor = async (uuid: string) => {
    setRemovingCompetitorUuid(uuid);
    try {
      const url = resolve('single_competitor_analysis', { uuid }, {});

      const response = await axiosClient.delete(url);

      if (response?.data === '') {
        lomavisShowToastr(t('competitor_removed_successfully'), 'success');
        setAllCompetitors((prev) => prev.filter((c) => c.uuid !== uuid));
      } else {
        console.log('No competitor data found after deletion.');
      }
    } catch (err: any) {
      lomavisShowToastr(
        err.response?.data?.message || err.message || err,
        'error'
      );
      console.error('Error deleting competitor:', err);
    } finally {
      setRemovingCompetitorUuid(null);
    }
  };

  const addCompetitor = async (competitorData: {
    platform_id: string;
    username: string;
  }) => {
    setSubmittingCompetitor(true);
    try {
      const platform = activePlatforms[selectedPlatform];

      // 🚫 Prevent duplicates
      const exists = allCompetitors.some((c) => {
        if (platform === INSTAGRAM) {
          return (
            c.platform === INSTAGRAM &&
            c.username?.toLowerCase() === competitorData.username.toLowerCase()
          );
        }
        if (platform === FACEBOOK) {
          return (
            c.platform === FACEBOOK &&
            c.platform_id === competitorData.platform_id
          );
        }
        return false;
      });

      if (exists) {
        lomavisShowToastr(
          t('lomavisCompetitorAnalysis.competitor_already_exists'),
          'error'
        );
        return; // stop here
      }
      // check if maximum profile allowed is not exceeded
      if (
        allowedCompetitorProfiles &&
        allCompetitors?.length >= allowedCompetitorProfiles
      ) {
        lomavisShowToastr(
          t('lomavisCompetitorAnalysis.max_competitor_count_exceeded'),
          'error'
        );
        return; // stop here
      }
      const url = resolve('competitor_analysis', {}, {});

      const response = await axiosClient.post(url, {
        location_uuid: locationDetails?.uuid,
        platform: activePlatforms[selectedPlatform],
        platform_id: competitorData?.platform_id,
        username: competitorData?.username
      });

      if (response?.data) {
        const newCompetitor = response.data;
        lomavisShowToastr(t('competitor_added_successfully'), 'success');
        setAllCompetitors((prev) => [...prev, response.data]);

        // Refresh this competitor 6 times, every 30 seconds
        let count = 0;
        const intervalId = setInterval(() => {
          count += 1;
          refreshSingleCompetitor(newCompetitor.uuid);
          if (count >= 6) clearInterval(intervalId);
        }, 30000);
      } else {
        console.log('No competitor data returned after adding.');
      }
    } catch (err: any) {
      console.error('Error adding competitor:', err);
      if (activePlatforms[selectedPlatform] !== INSTAGRAM) {
        lomavisShowToastr(
          err.response?.data?.message || err.message || err,
          'error'
        );
      }
      throw err;
    } finally {
      setSubmittingCompetitor(false);
    }
  };

  const refreshSingleCompetitor = async (uuid: string) => {
    try {
      const url = resolve('single_competitor_analysis', { uuid }, {});
      const { data } = await axiosClient.get(url);
      // Update only that competitor in allCompetitors

      setAllCompetitors((prev) =>
        prev.map((c) => (c.uuid === uuid ? data : c))
      );
    } catch (err: any) {
      console.error('Error refreshing competitor:', err);
    }
  };

  const getAllowedCompetitorCount = async () => {
    try {
      const url = resolve('current_subscription', {}, {});
      const { data } = await axiosClient.get(url);
      // Update only that competitor in allCompetitors
      setAllowedCompetitorProfiles(
        data?.social_media_management_subscription?.competitor_analysis_profiles
      );
    } catch (err: any) {
      console.error('Error refreshing competitor:', err);
    }
  };

  const navBarHeight =
    document.getElementById('kt_app_header')?.clientHeight + 15 * 2 + 50;

  return (
    <Container
      style={{
        height: `calc(100vh - ${navBarHeight}px)`
      }}
      className="w-100"
    >
      <div className="m-2" />
      {platformInformation &&
      !loading &&
      !platformInformation?.[FACEBOOK] &&
      !platformInformation?.[INSTAGRAM] ? (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center ">
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
            <span className="fs-3">
              {t('lomavisCompetitorAnalysis.no_FB_or_IG')}
            </span>
            <div className="d-flex gap-3 justify-content-center">
              {' '}
              <LomavisPlatformIcon platform={INSTAGRAM} size={40} />{' '}
              <LomavisPlatformIcon platform={FACEBOOK} size={40} />{' '}
            </div>
          </div>
        </div>
      ) : (
        <AnalyticsPagesLayout
          actions={COMPETITOR_ANALYTICS_NAVBAR_ACTIONS}
          commonHeader={
            <CommonHeader
              platforms={activePlatforms}
              selectedPlatform={activePlatforms[selectedPlatform]}
              setSelectedPlatform={setSelectedPlatform}
              platformInformation={platformInformation}
              allCompetitors={filteredCompetitors}
              setDate={(date) => {
                setDateRange(date);
              }}
              removeCompetitor={removeCompetitor}
              addCompetitor={addCompetitor}
              submittingCompetitor={submittingCompetitor}
              removingCompetitorUuid={removingCompetitorUuid}
              loadingCompetitors={loading && allCompetitors?.length === 0}
              totalCompetitors={allCompetitors?.length}
              maxCompetitors={allowedCompetitorProfiles}
              instagram_direct_connect={
                platformInformation?.[INSTAGRAM]?.[0]?.instagram_direct_connect
              }
            />
          }
          tabs={{
            [COMPETITOR_ANALYSIS]: (
              <CompetitorAnalysis
                competitors={filteredCompetitors}
                selectedPlatform={activePlatforms[selectedPlatform]}
                loadingCompetitors={loading && allCompetitors?.length === 0}
                platformInformation={platformInformation}
                totalCompetitors={allCompetitors?.length}
                maxCompetitors={allowedCompetitorProfiles}
                dateRange={dateRange}
              />
            ),
            [SOCIAL_FEEDS]: (
              <SocialFeeds
                competitors={filteredCompetitors}
                selectedPlatform={activePlatforms[selectedPlatform]}
                loadingCompetitors={loading && allCompetitors?.length === 0}
                totalCompetitors={allCompetitors?.length}
                maxCompetitors={allowedCompetitorProfiles}
              />
            )
          }}
        />
      )}
    </Container>
  );
};

export default index;
